"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Application } from "@splinetool/runtime";
import { SplineScene } from "@/components/ui/splite";

const SCENE_URL =
  "https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode";

export function HeroSpline() {
  const [status, setStatus] = useState<"loading" | "loaded" | "failed">(
    "loading",
  );
  // The Spline runtime is ~550 KiB of JS; hold it back until the browser is
  // idle so it doesn't compete with hydration and first paint.
  const [ready, setReady] = useState(false);
  const appRef = useRef<Application | null>(null);

  useEffect(() => {
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(() => setReady(true), {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(() => setReady(true), 200);
    return () => window.clearTimeout(id);
  }, []);

  const handleLoad = useCallback((app: Application) => {
    appRef.current = app;
    app.renderOnDemand = false;
    app.setGlobalEvents(true);
    setStatus("loaded");
  }, []);

  const handleError = useCallback(() => {
    setStatus("failed");
  }, []);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!finePointer.matches) return;

    const onMove = () => {
      appRef.current?.requestRender();
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  if (status === "failed") {
    // Scene unavailable (offline, blocked network): show a quiet decorative
    // glow instead of the 3D scene so the hero still reads as designed.
    return (
      <div className="hero-spline-wrap" aria-hidden="true">
        <div className="hero-spline-fallback" />
      </div>
    );
  }

  return (
    <div
      className="hero-spline-wrap"
      role="img"
      aria-label="Interactive 3D scene"
    >
      {status === "loading" && (
        <div className="spline-loader" aria-live="polite">
          LOADING_SCENE
        </div>
      )}
      <div
        className="hero-spline-canvas"
        style={{
          opacity: status === "loaded" ? 1 : 0,
          transition: "opacity 0.4s ease",
        }}
      >
        {ready && (
          <SplineScene
            scene={SCENE_URL}
            className="h-full w-full"
            onLoad={handleLoad}
            onError={handleError}
            renderOnDemand={false}
          />
        )}
      </div>
    </div>
  );
}
